import { Component, EventEmitter, Injector, OnDestroy, OnInit, Output } from '@angular/core';
import {
    Aangifte, AanslagBtw, AanslagVpb, AanslagZwv,
    BerichtenService, BerichtSbt,
    BokkzAlgDataService,
    BokkzClient, BtwAangifte,
    IntermediaryEntity, OphaalResponse, PdfRequest, PdfRequestOptie, PdfRequestSoort,
    PdfRequestType,
    ServiceBerichtEntity, Toeslagen, VpbAangifte
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormBuilder } from '@angular/forms';
import { ToastService } from '@snelstart/ui-toast';
import { SharedUiTableModule, TableConfiguration, ZeroStateConfiguration } from '@snelstart/ui-table';
import { SearchCriteria, SearchResult } from '@snelstart/domain-logic';
import { Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiIconModule } from '@snelstart/ui-icon';
import { SharedFeatureNavigationBarModule } from '@snelstart/feature-navigation-bar';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiTooltipModule } from '@snelstart/ui-tooltip';
import {SharedUiBadgeModule} from "@snelstart/ui-badge";

@Component({
    selector: 'snelstart-venture-fiscaal-feature-tax-sba-list',
    templateUrl: './feature-tax-sba-list.component.html',
    styleUrl: './feature-tax-sba-list.component.scss',
  imports: [SharedUiTableModule, CommonModule, SharedUiButtonModule, SharedUiIconModule, SharedFeatureNavigationBarModule, SharedUiDialogModule, SharedUiTooltipModule, SharedUiBadgeModule]
})
export class FeatureTaxSbaListComponent implements OnInit, OnDestroy {

    @Output() public details = new EventEmitter<string>();
    protected tableConfig!: TableConfiguration<SearchCriteria, ServiceBerichtEntity>;
    private destroy$ = new Subject<void>();
    public intermediairyEntity = {} as IntermediaryEntity;
    public serviceBerichtEntity = {} as ServiceBerichtEntity;
    public showTable = false;
    public year = {} as number;
    public dialogInfoVisible = false;

    constructor(
        public algDataService: BokkzAlgDataService,
        private injector: Injector,
        public bokkzClient: BokkzClient,
        public toastService: ToastService,
        public fb: FormBuilder,
    ) {
    }

    ngOnInit(): void {
        this.algDataService.intermediary.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.intermediairyEntity = value;
            this.showTable = false;
            setTimeout(() => {
                this.createTableConfig();
                this.showTable = true;
            }, 0);
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private createTableConfig(): void {
        this.tableConfig = TableConfiguration.create<SearchCriteria, ServiceBerichtEntity>({
            identifier: 'id',
            options: {
                useSearch: true,
                useSort: false
            },
            criteria: {take: 25, skip: 0, sortDescending: false},
            search: (criteria) => {
                return this.bokkzClient.getSbaOverzicht().pipe(
                    switchMap(response => {
                        if (!response) {
                            return new Observable<SearchResult<ServiceBerichtEntity>>(subscriber => {
                                subscriber.next(new SearchResult([], 0));
                                subscriber.complete();
                            });
                        }

                        let result = (response || []).filter(item => !item.gecontroleerd);

                        if (criteria.searchTerm && criteria.searchTerm.trim() !== '') {
                            const zoekterm = criteria.searchTerm.toLowerCase().trim();
                            result = result.filter(item =>
                                item.clientName?.toLowerCase().includes(zoekterm) ||
                                item.aanslagnummer?.toLowerCase().includes(zoekterm) ||
                                item.year?.toString().toLowerCase().includes(zoekterm) ||
                                item.type?.toLowerCase().includes(zoekterm)
                            );
                        }

                        return new Observable<SearchResult<ServiceBerichtEntity>>(subscriber => {
                            subscriber.next(new SearchResult(result, result?.length));
                            subscriber.complete();
                        });
                    })
                );
            },

            details: (entity) => {
                if (entity.serviceBerichtXbrl != undefined) {
                    this.algDataService.updateServiceBerichtXbrl(entity.serviceBerichtXbrl);
                    this.algDataService.updateServiceBericht(entity);
                    this.serviceBerichtEntity = entity;
                    if (this.berichtSoortBepaler(entity.serviceBerichtXbrl).includes('IB')
                        || this.berichtSoortBepaler(entity.serviceBerichtXbrl).includes('ZVW')
                        || this.berichtSoortBepaler(entity.serviceBerichtXbrl).includes('VPB')
                        || this.berichtSoortBepaler(entity.serviceBerichtXbrl).includes('TSL')) {
                        this.details.next(String(entity.id!));
                    }
                }
            },

            zeroStateConfig: ZeroStateConfiguration.create({
                icon: 'book-open-cover',
                mainText: 'Geen serviceberichten beschikbaar',
                subText: 'Er zijn geen niet-gecontroleerde serviceberichten gevonden.'
            })
        });
    }

    public getType(type: string) {
        const test = type.toLowerCase().replaceAll('_', ' ');
        return test[0].toUpperCase() + test.slice(1);
    }

    public berichtSoortBepaler(xbrl: string): string {
        if (xbrl.includes('bd-rpt-ih-sba')) {
            return 'IB';
        } else if (xbrl.includes('bd-rpt-zvw-sba')) {
            return 'ZVW';
        } else if (xbrl.includes('bd-rpt-vpb-sba')) {
            return 'VPB';
        } else if (xbrl.includes('bd-rpt-tsl-sbt')) {
            return 'TSL';
        } else if (xbrl.includes('bd-rpt-ihz-via')) {
            return 'VIA';
        } else if (xbrl.includes('bd-rpt-ob-sba')) {
            return 'BTW';
        } else if (xbrl.includes('uitstel-sba')) {
            return 'UITSTEL';
        } else return '-';
    }

    public getBedrag(xbrl: string): string {
        if (xbrl.includes('bd-rpt-ih-sba')) {
            return this.extractTaxAssessmentValue(xbrl, 'bd-i:TaxAssessmentAmountToBeReceivedToBePaidBalance');
        } else if (xbrl.includes('bd-rpt-zvw-sba')) {
            return this.extractTaxAssessmentValue(xbrl, 'bd-i:TaxAssessmentAmountToBeReceivedToBePaidBalance');
        } else if (xbrl.includes('bd-rpt-vpb-sba')) {
            return this.extractTaxAssessmentValue(xbrl, 'bd-i:TaxAssessmentAmountToBeReceivedToBePaidBalance');
        } else if (xbrl.includes('bd-rpt-tsl-sbt')) {
            return '-';
        } else if (xbrl.includes('bd-rpt-ihz-via')) {
            return '-';
        } else if (xbrl.includes('bd-rpt-ob-sba')) {
            return this.extractTaxAssessmentValue(xbrl, 'bd-i:ToBeSettledlToBeReceived');
        } else if (xbrl.includes('uitstel-sba')) {
            return '-';
        } else return '-';
    }

    public extractTaxAssessmentValue(input: string, search: string): string {
        const parser = new DOMParser();
        const doc = parser.parseFromString(input, 'application/xml');

        const element = doc.getElementsByTagName(search)[0];

        if (!element?.textContent) {
            return '-';
        }

        return new Intl.NumberFormat('de-DE', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(Number(element.textContent));
    }

    public isGecontroleerd(record: ServiceBerichtEntity): boolean {
        return this.algDataService.isGecontroleerd(record.id!);
    }

    public saveForm(record: ServiceBerichtEntity, event: Event): void {
        event.stopPropagation();

        setTimeout(() => {
            const nieuweStatus = !this.isGecontroleerd(record);

            this.serviceBerichtEntity = {
                ...record,
                gecontroleerd: nieuweStatus
            } as ServiceBerichtEntity;

            this.bokkzClient.postOneSba(this.serviceBerichtEntity).subscribe(value => {
                if (nieuweStatus) {
                    this.algDataService.markeerAlsGecontroleerd(record.id!);
                    this.toastService.showSuccessMessage('Het bericht is gemarkeerd als "Gecontroleerd".');
                } else {
                    this.algDataService.markeerAlsNietGecontroleerd(record.id!);
                    this.toastService.showInfoMessage('Het bericht is gemarkeerd als "Te controleren".');
                }
            });
        }, 0);
    }

    public toonPdf(record: ServiceBerichtEntity, event: Event) {
        event.stopPropagation();
        const bokkzClient = this.injector.get(BokkzClient);
        const berichtenService = this.injector.get(BerichtenService);

        if (record && record.serviceBerichtXbrl && record.aanslagnummer) {
            const ophaal = this.berichtSoortBepalerVoorPdf(record.serviceBerichtXbrl);
            berichtenService.converteerXbrlToJson(ophaal).subscribe((data) => {
                const pdfRequest = this.pdfSbaBepaler(data, ophaal);
                const pdfAntwoord = bokkzClient.getPdfRapport(pdfRequest);
                pdfAntwoord.subscribe((pdfStringBase64) => {
                    const blob = this.dataURItoBlob(pdfStringBase64);
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    const belastingSoort = this.getBelastingSoort(record.aanslagnummer!);
                    const filenameWithoutExtension = record.clientName + ' - Aanslag ' + belastingSoort + ' ' + record.year;
                    link.download = `${filenameWithoutExtension}.pdf`;
                    link.href = url;
                    document.body.appendChild(link);
                    link.click();
                });
            });
        }
    }

    getBelastingSoort(aanslagnummer: string): string {
        const match = aanslagnummer.match(/[A-Za-z]/);
        if (!match) return '';

        const letter = match[0].toUpperCase();

        const belastingMap: { [key: string]: string } = {
            'H': 'Inkomstenbelasting',
            'W': 'Zorgverzekeringswet',
            'V': 'Vennootschapsbelasting',
            'T': 'Toeslagen',
            'B': 'Omzetbelasting',
            'F': 'Omzetbelasting',
            'O': 'Omzetbelasting',
        };

        return belastingMap[letter] || '';
    }

    dataURItoBlob(dataURI: string) {
        if (dataURI != null) {
            const byteString = atob(dataURI.replace(/"/g, ''));
            const arrayBuffer = new ArrayBuffer(byteString.length);
            const int8Array = new Uint8Array(arrayBuffer);
            for (let i = 0; i < byteString.length; i++) {
                int8Array[i] = byteString.charCodeAt(i);
            }
            return new Blob([int8Array], { type: 'application/zip' });
        } else throw 'No dataobject';
    }

    berichtSoortBepalerVoorPdf(xbrl: string): OphaalResponse {
        let ophaalResponse = {} as OphaalResponse;
        if (xbrl.includes('bd-rpt-ih-sba')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'IB'
            };
        } else if (xbrl.includes('bd-rpt-zvw-sba')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'ZVW'
            };
        } else if (xbrl.includes('bd-rpt-vpb-sba')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'VPB'
            };
        } else if (xbrl.includes('bd-rpt-tsl-sbt')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'TSG'
            };
        } else if (xbrl.includes('bd-rpt-ihz-via')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'VIA'
            };
        } else if (xbrl.includes('bd-rpt-ob-sba')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'OB'
            };
        } else if (xbrl.includes('uitstel-sba')) {
            ophaalResponse = {
                inhoud: btoa(xbrl),
                berichtsoort: 'BECON'
            };
        }
        return ophaalResponse;
    }

    pdfSbaBepaler(data: Aangifte | Toeslagen | VpbAangifte | BtwAangifte, ophaal: OphaalResponse): PdfRequest {
        const pdfRequest = {} as PdfRequest;
        if (ophaal.berichtsoort.includes('IB')) {
            pdfRequest.aanslagIbJaar1 = data;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aanslag;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.IB;
        } else if (ophaal.berichtsoort.includes('ZVW')) {
            pdfRequest.aanslagZvwJaar1 = data as AanslagZwv;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aanslag;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.ZVW;
        } else if (ophaal.berichtsoort.includes('VPB')) {
            pdfRequest.aanslagVpbJaar1 = data as AanslagVpb;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aanslag;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.VPB;
        } else if (ophaal.berichtsoort.includes('TSG')) {
            pdfRequest.toeslagenJaar1 = data as BerichtSbt;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Beschikking;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.TSL;
        } else if (ophaal.berichtsoort.includes('OB')) {
            pdfRequest.aanslagBtwJaar1 = data as AanslagBtw;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Aanslag;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.BTW;
        } else if (ophaal.berichtsoort.includes('VIA')) {
            pdfRequest.aangifteJaar1 = data as Aangifte;
            pdfRequest.concept = false;
            pdfRequest.soort = PdfRequestSoort.Via;
            pdfRequest.optie = PdfRequestOptie.GeheleRapport;
            pdfRequest.type = PdfRequestType.IB;
        } else if (ophaal.berichtsoort.includes('BECON')) {
            pdfRequest.aanslagZvwJaar1 = data as AanslagZwv;
            pdfRequest.concept = false;
        }
        return pdfRequest;
    }


}
